import { FileDownloadOutlined } from '@mui/icons-material';
import { Permissions, useScopeChecker } from 'src/rbac';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import dashboardServices from 'src/services/apiServices/dashboard-services/dashboard-services';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  CardHeader,
  Chip,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  TableContainer,
  TableSortLabel,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Edit02Icon from '@untitled-ui/icons-react/build/esm/Edit02';
import PlusIcon from '@untitled-ui/icons-react/build/esm/Plus';
import { format } from 'date-fns';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { SignatureAction } from 'src/types/signatureContext';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import DischargeRecheckModal from 'src/components/common-modals/discharge-recheck-modal';
import { getRecheckDischargeDecision } from 'src/components/document-management/recheck-discharge-gate';
import { Scrollbar } from 'src/components/scrollbar';
import {
  DocDuePnAlert,
  multiPayerFlag,
  recheckDischarge,
  therapistBulkSignature,
} from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import useClockInOutButtonVisibility from 'src/hooks/use-clock-in-out-button-visibility';
import RightIcon from 'src/icons/untitled-ui/duocolor/rightIcon';
import { useStopwatch } from 'src/layouts/check-in-checkout/StopwatchContext';
import useEffectAfterMount from 'src/layouts/components/useEffectAfterMount';
import emptyImg from 'src/pages/therapist-dashboard/therapist-document/image-removebg-preview.png';
import { paths } from 'src/paths';
import {
  checkPendingDocsAlertAction,
  getTherapistDocumentCreateEditDataAction,
  resetApproveRecheckDischarge,
  resetPendingDocsAlertAction,
  resetTherapistDocumentCreateEditDataAction,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import {
  getListOfTherapistAvailability,
  resetListOfTherapistAvailability,
} from 'src/redux/actions/myAvailabilityAction';
import {
  getAllPatientsByAction,
  getPatientIdAction,
  resetPatientRecordAction,
  switchTherapistDisciplineAction,
} from 'src/redux/actions/patientAction';
import {
  getAllTodaysDocumentAction,
  previewDashboardDoc,
  resetDashboardSubmittedDoc,
  viewSignatureDashboardDoc,
} from 'src/redux/actions/therapist-dashboard-action';
import { getDisplayString, getDueDateSubText, getVisitsLabel } from 'src/utils/getDaysBtDate';
import LoaderIcon from '../../../../src/components/LoaderImg/loader.gif';
import useDialogVisibility from '../../../utils/dialog-hide';
import { ClipBoard, DocumentIcons } from '../dashboard-icon';
import ClockInOutModal from '../time-in-out/clock-in-out-modal';
import { DisciplineType, docStatusTypes, documentTypes } from './document-manegment-const';
import { MenuDateFilter } from './menu-date-filter';
import MultiSelectWithSearch from './table-header-multiselect-search';
import { TableHeaderMultiselect } from './table-hearder-multiselect';
import WarningModal from 'src/components/therapyPlanning_2.0/commonComponent/warning-modal';
import { shouldShowPayerWarning } from 'src/theme/utils';

const DocumentManagement: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isRunning, startStopwatch } = useStopwatch();
  const containerRef = useRef<HTMLDivElement>(null);

  const { canShowDialog: canShowClockInDialog } = useDialogVisibility('clock_in_do_not_show');
  const [isDocDuePNAlert] = useFeatureToggleWhenReady(DocDuePnAlert, 'FACILITY');
  // THIN-8323: export format picker
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exportingFormat, setExportingFormat] = useState<'pdf' | 'csv' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [isTherapistBulkSignature] = useFeatureToggleWhenReady(
    therapistBulkSignature,
    'ENTERPRISE'
  );

  const getDocumentRes = useSelector((state: any) => state.dashboardDocumentListReducer);
  const checkoutResponse = useSelector((state: any) => state.checkoutReducer);
  const [providerId] = useCurrentUser(['providerId', 'firstName', 'lastName']);
  const { patients, patientDetails, episodeOfCare, details } = paths;
  const profileData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data
  );

  const has = useScopeChecker();

  const totalPatientRes = useSelector((state: any) => state.getAllPatientsReducer);

  // APIT-587: clock in/out warning pop-ups (document editing + signature) are gated
  // by the per-facility flag (single source of truth) instead of the enterprise
  // enablePaycom/enableHostedTime/enablePaylocity flags. true = show warning.
  const { refetch: refetchClockInOutVisibility } = useClockInOutButtonVisibility();
  const [isMultiPayer]: any = useFeatureToggleWhenReady(multiPayerFlag, 'FACILITY');

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'dueDate',
    direction: 'asc',
  });
  const [isRecheckDischarge] = useFeatureToggleWhenReady(recheckDischarge, 'ENTERPRISE');
  const checkPendingDocsAlert = useSelector((state: any) => state.checkPendingDocsAlert);

  const [showDischargeModal, setShowDischargeModal] = useState<boolean>(false);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  //clock in modal

  const [isClockIn, setIsClockIn] = useState(false);
  const [notToShowAgain, setNotToShowAgain] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>('');

  useEffect(() => {
    dispatch(getListOfTherapistAvailability());
  }, []);

  useEffect(() => {
    if (!checkPendingDocsAlert?.isSuccess && checkPendingDocsAlert?.response === null) return;
    if (!checkPendingDocsAlert?.isSuccess && checkPendingDocsAlert?.response !== null) return;

    // THIN-7875: this entry point used to test `response?.length` on what is an OBJECT —
    // { documents, recheckDischargeApproval } since THIN-7586 — so the test was permanently
    // undefined, the modal never opened, and ReCheck did nothing at all from the dashboard for
    // every tenant with the toggle on. Both entry points now read the response through the same
    // helper so they cannot drift on its shape again.
    const { canSkipModal } = getRecheckDischargeDecision(checkPendingDocsAlert?.response);

    // Already approved, or nothing outstanding → straight to the discharge, matching the
    // documents-list path. Anything else opens the modal, which decides on its own whether this
    // user may proceed, approve, or is limited to dismissing it.
    if (canSkipModal) {
      // Clear the consumed pending-docs response so it can't re-trigger this effect.
      dispatch(resetPendingDocsAlertAction());
      navigate('/document/document-edit', {
        state: {
          document: selectedDocument,
        },
      });
      return;
    }

    setShowDischargeModal(true);
  }, [checkPendingDocsAlert]);

  // THIN-7875: clear the discharge/approve state when leaving the dashboard, as the documents-list
  // path already does. The navigate above resets the pending-docs response, but the modal's own
  // Continue/Approve path does not — and a stale `isSuccess` approval left in the store makes the
  // next modal auto-continue on mount, which would re-open the same bypass this ticket closes.
  useEffect(() => {
    return () => {
      dispatch(resetPendingDocsAlertAction());
      dispatch(resetApproveRecheckDischarge());
    };
  }, []);

  const navigateToSignature = useCallback(
    (document?: any) => {
      navigate(`/dashboard/doc-sign`, {
        state: {
          document,
        },
      });
    },
    [selectedDocument]
  );

  const handleSignatureNavigation = async (formId: any) => {
    setActionToBeContinue('SIGN_DOCUMENT');
    setSelectedDocument(formId);
    // Re-fetch the per-facility flag NOW so this operation uses the latest value
    // (no page/tab refresh needed). Any error/404 -> false (hide).
    const canShowClockInOut = await refetchClockInOutVisibility();
    if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
      setIsClockIn(true);
      return;
    }
    dispatch(viewSignatureDashboardDoc(formId));
    navigateToSignature(formId);
  };

  const previewDocument = (formId: any) => {
    dispatch(previewDashboardDoc(formId));
    navigate('/dashboard/doc');
  };

  const [actionToBeContinue, setActionToBeContinue] = useState('');

  const handleDocumentEdit = useCallback(
    async (document: any) => {
      setSelectedDocument(document);
      setActionToBeContinue('EDIT_MY_DOCUMENT');

      // Re-fetch the per-facility flag NOW so this edit uses the latest value
      // (no page/tab refresh needed). Any error/404 -> false (hide).
      const canShowClockInOut = await refetchClockInOutVisibility();
      if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
        setIsClockIn(true);
        return;
      }
      dispatch(resetTherapistDocumentCreateEditDataAction());
      dispatch(getTherapistDocumentCreateEditDataAction(document));

      if (shouldShowPayerWarning(document, document?.isPayerPresent, isMultiPayer)) {
        setIsWarningModalOpen(true);
        return;
      }

      // discharge doc prevent
      if (
        isRecheckDischarge &&
        document?.formType?.includes('DISCHARGE') &&
        !document?.submissionId
      ) {
        dispatch(
          checkPendingDocsAlertAction({
            eocId: document?.episodeOfCareId,
            params: {
              discipline: document?.type,
              localDate: moment(new Date()).format('YYYY-MM-DD'),
            },
          })
        );
        return;
      }

      navigate('/document/document-edit', {
        state: {
          document,
        },
      });
    },
    [isRunning, notToShowAgain, refetchClockInOutVisibility, isRecheckDischarge]
  );

  const checkEditPermission = (document: any) => {
    // Prefer signatureContext (THIN-7160) — CAN_EDIT on the therapist axis for the form
    // author or on the supervisor axis for the assigned cosigner doing a cosigner-edit
    // (THIN-6967). Falls back to legacy form-type-permission gates when ctx is missing.
    const ctx = document?.signatureContext;
    if (ctx) {
      return (
        ctx.allowedTherapistSignatureActions?.includes(SignatureAction.CAN_EDIT) ||
        ctx.allowedSupervisorSignatureActions?.includes(SignatureAction.CAN_EDIT)
      );
    }
    if (
      document.formType?.includes('RECERTIFICATION') &&
      !has(Permissions.UPDATE_RECERTIFICATION)
    ) {
      return false;
    }
    if (document.formType?.includes('EVALUATION') && !has(Permissions.UPDATE_EVAL)) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (checkoutResponse.isGetCheckIn && checkoutResponse?.response?.content !== null) {
      if (checkoutResponse?.clockAction === 'CLOCK_IN_SUCCESS') {
        if (actionToBeContinue === 'SIGN_DOCUMENT') {
          dispatch(viewSignatureDashboardDoc(selectedDocument));
          navigateToSignature(selectedDocument);
          return;
        }
        if (actionToBeContinue === 'EDIT_MY_DOCUMENT') {
          dispatch(resetTherapistDocumentCreateEditDataAction());
          dispatch(getTherapistDocumentCreateEditDataAction(selectedDocument));

          if (
            shouldShowPayerWarning(selectedDocument, selectedDocument?.isPayerPresent, isMultiPayer)
          ) {
            setIsClockIn(false);
            setIsWarningModalOpen(true);
            return;
          }

          // discharge doc prevent
          if (
            isRecheckDischarge &&
            selectedDocument?.formType?.includes('DISCHARGE') &&
            !selectedDocument?.submissionId
          ) {
            dispatch(
              checkPendingDocsAlertAction({
                eocId: selectedDocument?.episodeOfCareId,
                params: {
                  discipline: selectedDocument?.type,
                  localDate: moment(new Date()).format('YYYY-MM-DD'),
                },
              })
            );
            return;
          }

          navigate('/document/document-edit', {
            state: {
              document: selectedDocument,
            },
          });
        }
      }
    }
  }, [checkoutResponse]);

  const handleIsClock = () => {
    if (actionToBeContinue === 'SIGN_DOCUMENT') {
      dispatch(viewSignatureDashboardDoc(selectedDocument));
      navigateToSignature(selectedDocument);
      return;
    }
    if (actionToBeContinue === 'EDIT_MY_DOCUMENT') {
      dispatch(resetTherapistDocumentCreateEditDataAction());
      dispatch(getTherapistDocumentCreateEditDataAction(selectedDocument));

      if (
        shouldShowPayerWarning(selectedDocument, selectedDocument?.isPayerPresent, isMultiPayer)
      ) {
        setIsClockIn(false);
        setIsWarningModalOpen(true);
        return;
      }

      if (
        isRecheckDischarge &&
        selectedDocument?.formType?.includes('DISCHARGE') &&
        !selectedDocument?.submissionId
      ) {
        dispatch(
          checkPendingDocsAlertAction({
            eocId: selectedDocument?.episodeOfCareId,
            params: {
              discipline: selectedDocument?.type,
              localDate: moment(new Date()).format('YYYY-MM-DD'),
            },
          })
        );
        return;
      }
      navigate('/document/document-edit', {
        state: {
          document: selectedDocument,
        },
      });
    }
    setNotToShowAgain(true);
    setIsClockIn(false);
  };
  const listOfTherapistAvailability = useSelector(
    (state: any) => state.getListOfTherapistAvaiReducer
  );

  useEffect(() => {
    if (profileData && listOfTherapistAvailability?.response?.data?.length) {
      dispatch(resetDashboardSubmittedDoc());
      const mappedTherapists = listOfTherapistAvailability?.response?.data
        ?.map((res: any) => {
          return {
            label:
              res?.uuid === providerId
                ? `${res?.lastName}, ${res?.firstName} (Me)`
                : `${res?.lastName}, ${res?.firstName}`,
            value: res?.uuid,
          };
        })
        ?.sort((a: any, b: any) => {
          return a?.label.localeCompare(b?.label);
        });
      setSelectedTherapist(mappedTherapists);
      if (!has(Permissions.DEFAULT_FILTER_FOR_MY_DASHBOARD_DOR_DOCS_DUE)) {
        setSelectedDisciples([
          {
            label: profileData?.discipline,
            value: profileData?.discipline,
          },
        ]);
        setSelectedStatus([
          { label: 'Due Today', value: 'TODAY' },
          { label: 'Overdue', value: 'OVERDUE' },
        ]);
      }
    }
  }, [profileData, listOfTherapistAvailability]);

  const goToEoc = (document: any) => {
    dispatch(getPatientIdAction(document?.patientId));
    dispatch(switchTherapistDisciplineAction(''));
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareId: document?.episodeOfCareId,
        getPatientId: document?.patientId,
      },
    });
  };

  const [getCurrentItem, setGetCurrentItem] = useState<any>(null);

  const HandleStartClock = () => {
    const millageInfo = {
      locationType: 'BUILDING',
      payByMilage: null,
    };
    startStopwatch('MILLAGE', millageInfo);
  };

  const [selectedTherapist, setSelectedTherapist] = useState<any>([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState<any>([]);
  const [selectedDisciples, setSelectedDisciples] = useState<any>([]);
  const [selectedPatient, setSelectedPatient] = useState<any>([]);
  const [selectedStatus, setSelectedStatus] = useState<any>([]);
  const [selectedDueDate, setSelectedDueDate] = useState<any>({
    startDate: '',
    endDate: '',
  });

  const getListOfValues = (items: any) => {
    return items?.map((therapist: any) => therapist?.value);
  };

  /**
   * THIN-8323: the one filter object this screen is drawn with.
   *
   * The table and the exports both use it, so an export cannot describe a
   * different list than the one on screen (IS-9805). Only paging differs: the
   * table reads a page at a time, the export covers the whole filtered list and
   * lets the server decide how much it will serve.
   */
  const buildDocumentFilters = () => ({
    therapistIds: selectedTherapist?.length
      ? getListOfValues(selectedTherapist)
      : getListOfValues(getTherapistList),
    documentTypes: selectedDocumentType?.length ? getListOfValues(selectedDocumentType) : null,
    types: selectedDisciples?.length ? getListOfValues(selectedDisciples) : null,
    dueStatus: selectedStatus?.length ? getListOfValues(selectedStatus) : null,
    startDate: selectedDueDate?.startDate
      ? moment(selectedDueDate?.startDate).format('YYYY-MM-DD')
      : null,
    endDate: selectedDueDate?.endDate
      ? moment(selectedDueDate?.endDate).format('YYYY-MM-DD')
      : null,
    patientIds: selectedPatient?.length ? getListOfValues(selectedPatient) : null,
    sortBy: sortConfig?.key,
    sortDirection: sortConfig?.direction,
  });

  const documentRequestParams = () => ({
    requestedDocType: isTherapistBulkSignature ? 'DOC_ALERTS' : null,
  });

  const getAllDocumentApiCall = (page: number) => {
    dispatch(
      getAllTodaysDocumentAction({
        param: { ...buildDocumentFilters(), page, pageSize: 20 },
        params: documentRequestParams(),
      })
    );
  };

  useEffect(() => {
    return () => {
      dispatch(resetListOfTherapistAvailability());
      dispatch(resetDashboardSubmittedDoc());
    };
  }, []);

  useEffectAfterMount(() => {
    if (!listOfTherapistAvailability?.isGetAvailability) return;
    if (isTherapistBulkSignature === null) return;
    dispatch(resetDashboardSubmittedDoc());
    getAllDocumentApiCall(0);
    // }
  }, [
    selectedDocumentType,
    selectedStatus,
    selectedDueDate,
    selectedPatient,
    selectedTherapist,
    selectedDisciples,
    sortConfig,
    isTherapistBulkSignature,
  ]);

  const getTherapistList = useMemo(() => {
    const mappedTherapists = listOfTherapistAvailability?.response?.data?.map((res: any) => {
      return {
        label:
          res?.uuid === providerId
            ? `${res?.lastName}, ${res?.firstName} (Me)`
            : `${res?.lastName}, ${res?.firstName}`,
        value: res?.uuid,
      };
    });

    return mappedTherapists?.sort((a: any, b: any) => {
      return a?.label.localeCompare(b?.label);
    });
  }, [listOfTherapistAvailability, providerId]);

  const [pageNo, setPageNo] = useState(0);

  useEffect(() => {
    dispatch(resetPatientRecordAction());
    dispatch(
      getAllPatientsByAction({
        data: {
          searchString: '',
        },
        params: {
          page: 0,
          pageSize: 20,
          active: true,
        },
      })
    );
    dispatch(resetUuidAction());
  }, []);
  const getAllPatientList = useMemo(() => {
    return totalPatientRes?.totalPatients.map((res: any) => {
      return {
        label: `${res?.lastName}, ${res?.firstName}`,
        value: res?.uuid,
      };
    });
  }, [totalPatientRes]);

  const handleSort = (key: string) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === 'asc';
    const direction = isAsc ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  /**
   * A CSV error arrives as a Blob, because the request asked for one. Read it back
   * out so the server's "narrow the filters" message is shown rather than swallowed.
   */
  const readErrorMessage = async (error: any): Promise<string | null> => {
    const body = error?.response?.data;
    if (!body) return null;
    if (body instanceof Blob) {
      try {
        return JSON.parse(await body.text())?.message ?? null;
      } catch {
        return null;
      }
    }
    return body?.message ?? null;
  };

  const fileNameFromDisposition = (response: any): string | null => {
    const disposition = response?.headers?.['content-disposition'];
    return disposition?.match(/filename="?([^"]+)"?/)?.[1] ?? null;
  };

  /**
   * THIN-8323: both formats are rendered by the server.
   *
   * Reliant's therapists have no spreadsheet application, so CSV alone is
   * unreadable to them. The old path re-fetched the list and built the CSV in the
   * browser, with no quoting and a page size guessed from the previous response.
   */
  /**
   * THIN-8352: a large Docs Due PDF render (up to ~2,500 rows) takes longer than the
   * 60s gateway timeout, so PDF is generated asynchronously. Start the job, poll until
   * it reaches a terminal status, and return the presigned download URL. CSV stays
   * synchronous (it is fast and already works to the cap).
   */
  const DOCS_DUE_POLL_INTERVAL_MS = 2500;
  const DOCS_DUE_POLL_TIMEOUT_MS = 5 * 60 * 1000;

  const exportDocsDuePdfAsync = async (payload: any): Promise<string | null> => {
    const startRes = await dashboardServices.startDocsDueExportAsync(payload, 'pdf');
    const jobId = startRes?.data?.data?.jobId;
    if (!jobId) throw new Error('Could not start the PDF export.');

    const deadline = Date.now() + DOCS_DUE_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, DOCS_DUE_POLL_INTERVAL_MS));
      const pollRes = await dashboardServices.getDocsDueExportJob(jobId);
      const view = pollRes?.data?.data;
      if (view?.status === 'SUCCEEDED') return view?.downloadUrl ?? null;
      if (view?.status === 'FAILED') throw new Error(view?.error || 'The PDF export failed.');
      // RUNNING (or a not-found/expired blip) -> keep polling until the deadline
    }
    throw new Error(
      'The PDF export is taking longer than expected. Please try again or narrow your filters.'
    );
  };

  const handleExport = async (format: 'pdf' | 'csv') => {
    setExportMenuAnchor(null);
    setExportingFormat(format);
    try {
      if (format === 'csv') {
        const response = await dashboardServices.exportDashboardDocuments(
          { param: buildDocumentFilters(), params: documentRequestParams() },
          'csv'
        );
        // The axios interceptor resolves with `error.response` instead of rejecting, so a
        // failed export (e.g. 400 "narrow the filters") lands here; route it to the catch.
        if (!response || response.status >= 400) throw { response };
        const url = URL.createObjectURL(new Blob([response?.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = fileNameFromDisposition(response) ?? 'Documents_Due.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // THIN-8352: the presigned URL only arrives after the async poll (seconds later), too late
        // to call window.open() then — it would be outside the click gesture and popup-blocked, so
        // nothing opens. Open the tab NOW, synchronously inside the gesture, and point it at the URL
        // once the render finishes. Falls back to this tab if the popup was blocked anyway.
        const pdfWindow = window.open('', '_blank');
        try {
          pdfWindow?.document.write(
            '<title>Generating PDF…</title><body style="font-family:sans-serif;padding:2rem;color:#334">Generating your Documents Due PDF… this can take up to a minute for a large list. This tab will open it automatically.</body>'
          );
        } catch {
          /* about:blank is same-origin, but ignore if the browser restricts writing */
        }
        try {
          const pdfUrl = await exportDocsDuePdfAsync({
            param: buildDocumentFilters(),
            params: documentRequestParams(),
          });
          if (pdfUrl) {
            if (pdfWindow) pdfWindow.location.href = pdfUrl;
            else window.location.href = pdfUrl;
          } else if (pdfWindow) {
            pdfWindow.close();
          }
        } catch (err) {
          if (pdfWindow) pdfWindow.close();
          throw err;
        }
      }
    } catch (error: any) {
      // The server refuses a list bigger than it will render and says why, so
      // show its message rather than a generic failure. Fall back to the error's
      // own message (async timeout / FAILED job) before the generic one.
      const message =
        (await readErrorMessage(error)) ??
        error?.message ??
        `Could not generate the ${format.toUpperCase()}. Please try again.`;
      setExportError(message);
      setTimeout(() => setExportError(null), 5000);
    } finally {
      setExportingFormat(null);
    }
  };
  const handleSortMultiselect = useCallback((label: any, direction: any) => {
    if (label === 'Therapist') return setSortConfig({ key: 'therapistName', direction });
    if (label === 'Document Type') return setSortConfig({ key: 'documentType', direction });
    if (label === 'Discipline') return setSortConfig({ key: 'discipline', direction });
    if (label === 'Status') return setSortConfig({ key: 'dueStatus', direction });
    if (label === 'PatientName') return setSortConfig({ key: 'patientName', direction });
  }, []);

  const handleScroll = () => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10 && !getDocumentRes?.loading) {
      if (getDocumentRes?.response?.data?.totalElements !== getDocumentRes?.totalFacility?.length) {
        getAllDocumentApiCall(getDocumentRes?.pageNumber);
      }
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title={
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6">Documents Due</Typography>
                  {getDocumentRes?.loading ? (
                    <Box sx={{ width: 100, ml: 3 }}>
                      <Typography
                        width={'100%'}
                        variant="h4"
                      >
                        <Skeleton animation="pulse" />
                      </Typography>
                    </Box>
                  ) : (
                    <Chip
                      label={`${
                        getDocumentRes?.response?.data?.totalElements
                          ? getDocumentRes?.response?.data?.totalElements
                          : '0'
                      } Document`}
                      variant="outlined"
                      sx={{ marginLeft: '10px' }}
                    />
                  )}
                </Box>
                <Box>
                  <LoadingButton
                    disabled={!!exportingFormat || getDocumentRes?.loading}
                    startIcon={<FileDownloadOutlined />}
                    variant="contained"
                    size="small"
                    sx={{
                      whiteSpace: 'nowrap',
                      height: 35,
                      width: 120,
                      ':hover': {
                        background: '#6366F1',
                      },
                      '.MuiLoadingButton-loadingIndicator': {
                        left: 15,
                      },
                    }}
                    loadingPosition="start"
                    onClick={(event) => setExportMenuAnchor(event.currentTarget)}
                    loading={!!exportingFormat}
                    data-testid="docs-due-download"
                  >
                    Download
                  </LoadingButton>
                  <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={() => setExportMenuAnchor(null)}
                  >
                    {/* PDF first: it is the format therapists without Office can read. */}
                    <MenuItem
                      onClick={() => handleExport('pdf')}
                      data-testid="docs-due-download-pdf"
                    >
                      PDF
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleExport('csv')}
                      data-testid="docs-due-download-csv"
                    >
                      CSV
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            </>
          }
        />

        <Scrollbar>
          <>
            <Stack gap="1rem">
              <TableContainer component={Paper}>
                <Box
                  ref={containerRef}
                  onScroll={handleScroll}
                  sx={{
                    maxHeight: 1200,
                    overflowY: 'auto',
                  }}
                >
                  <Table
                    stickyHeader
                    sx={{ minWidth: 700 }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === 'patientName'}
                            direction={
                              sortConfig.key === 'patientName' ? sortConfig.direction : 'asc'
                            }
                            onClick={() => handleSort('patientName')}
                          >
                            Patient
                          </TableSortLabel>
                          <MultiSelectWithSearch
                            options={getAllPatientList}
                            pageNo={pageNo}
                            setPageNo={setPageNo}
                            handleSortMultiselect={handleSortMultiselect}
                            setAppliedValues={setSelectedPatient}
                            appliedValues={selectedPatient}
                            sortConfig={sortConfig}
                          />
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === 'discipline'}
                            direction={
                              sortConfig.key === 'discipline' ? sortConfig.direction : 'asc'
                            }
                            onClick={() => handleSort('discipline')}
                          >
                            Discipline
                          </TableSortLabel>
                          <TableHeaderMultiselect
                            label="Discipline"
                            sortConfig={sortConfig}
                            appliedValues={selectedDisciples}
                            setAppliedValues={setSelectedDisciples}
                            options={DisciplineType}
                            handleSortMultiselect={handleSortMultiselect}
                          />
                        </TableCell>

                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === 'documentType'}
                            direction={
                              sortConfig.key === 'documentType' ? sortConfig.direction : 'asc'
                            }
                            onClick={() => handleSort('documentType')}
                          >
                            Document Type
                          </TableSortLabel>
                          <TableHeaderMultiselect
                            label="Document Type"
                            sortConfig={sortConfig}
                            appliedValues={selectedDocumentType}
                            setAppliedValues={setSelectedDocumentType}
                            options={documentTypes}
                            handleSortMultiselect={handleSortMultiselect}
                          />
                        </TableCell>

                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === 'therapistName'}
                            direction={
                              sortConfig.key === 'therapistName' ? sortConfig.direction : 'asc'
                            }
                            onClick={() => handleSort('therapistName')}
                          >
                            Therapist
                          </TableSortLabel>
                          <TableHeaderMultiselect
                            label="Therapist"
                            appliedValues={selectedTherapist}
                            sortConfig={sortConfig}
                            setAppliedValues={setSelectedTherapist}
                            options={getTherapistList}
                            handleSortMultiselect={handleSortMultiselect}
                          />
                        </TableCell>

                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === 'dueDate'}
                            direction={sortConfig.key === 'dueDate' ? sortConfig.direction : 'asc'}
                            onClick={() => handleSort('dueDate')}
                          >
                            Due date
                          </TableSortLabel>
                          <MenuDateFilter
                            label="Due Date"
                            setSelectedDueDate={setSelectedDueDate}
                            selectedDueDate={selectedDueDate}
                          />
                        </TableCell>

                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === 'dueStatus'}
                            direction={
                              sortConfig.key === 'dueStatus' ? sortConfig.direction : 'asc'
                            }
                            onClick={() => handleSort('dueStatus')}
                          >
                            Status
                          </TableSortLabel>
                          <TableHeaderMultiselect
                            label="Status"
                            appliedValues={selectedStatus}
                            sortConfig={sortConfig}
                            setAppliedValues={setSelectedStatus}
                            options={docStatusTypes}
                            handleSortMultiselect={handleSortMultiselect}
                          />
                        </TableCell>

                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {getDocumentRes?.totalFacility?.length === 0 &&
                      !getDocumentRes?.loading &&
                      !listOfTherapistAvailability?.loading ? (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Box
                              textAlign="center"
                              p={3}
                            >
                              <Box>
                                <img
                                  src={emptyImg}
                                  alt="loader"
                                  style={{
                                    width: 720,
                                    height: 300,
                                    margin: 'auto',
                                    alignItems: 'center',
                                    alignContent: 'center',
                                    marginTop: 100,
                                    display: 'block',
                                  }}
                                />

                                <Typography
                                  color={'text.secondary'}
                                  variant="h5"
                                  fontWeight={'200'}
                                >
                                  No results found
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        getDocumentRes?.totalFacility?.map((document: any) => {
                          return (
                            document.status !== 'COMPLETED' && (
                              <TableRow
                                hover
                                key={document?.uuid}
                              >
                                <TableCell>
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {document?.patientName}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2">{document?.type}</Typography>
                                </TableCell>

                                <TableCell>
                                  <Stack
                                    alignItems="center"
                                    direction="row"
                                    spacing={1}
                                  >
                                    <Grid
                                      container
                                      sx={{ alignItems: 'center' }}
                                    >
                                      <Box sx={{ marginRight: '4px', marginTop: '7px' }}>
                                        <DocumentIcons
                                          iconType={document?.formType
                                            ?.split('_')?.[0]
                                            ?.toLowerCase()}
                                        />
                                      </Box>

                                      <Box sx={{ display: 'flex' }}>
                                        <Typography variant="subtitle2">
                                          {document?.documentType}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Stack>
                                </TableCell>

                                <TableCell>
                                  <Typography variant="subtitle2">
                                    {' '}
                                    {document?.providerName}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    {/* THIN-7647: for progress-note rows (visits present) lead with
                                      "X / Y visits" and show the day-based deadline as sub-text;
                                      other doc types keep the plain due-date display. */}
                                    {isDocDuePNAlert && getVisitsLabel(document?.visits) ? (
                                      <>
                                        <Typography variant="subtitle2">
                                          {getVisitsLabel(document?.visits)}
                                        </Typography>
                                        <Typography
                                          sx={{ color: '#6C737F' }}
                                          variant="caption"
                                        >
                                          {getDueDateSubText(
                                            document?.dueDate,
                                            document?.alertReason,
                                            document?.visits
                                          )}
                                        </Typography>
                                      </>
                                    ) : (
                                      <>
                                        <Typography variant="subtitle2">
                                          {document?.dueDate
                                            ? format(
                                                new Date(
                                                  new Date(document?.dueDate)?.valueOf() +
                                                    new Date(
                                                      document?.dueDate
                                                    )?.getTimezoneOffset() *
                                                      60 *
                                                      1000
                                                ),
                                                'dd MMM, yyyy'
                                              )
                                            : '-'}
                                        </Typography>
                                        <Typography
                                          sx={{ color: '#6C737F' }}
                                          variant="caption"
                                        >
                                          {getDisplayString(
                                            document?.dueDate,
                                            false,
                                            document,
                                            document?.maxProgressNoteDays,
                                            '',
                                            isDocDuePNAlert
                                          )}
                                        </Typography>
                                      </>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', ml: '-20px' }}>
                                    {'OVERDUE' === document?.status ? (
                                      <Chip
                                        label={
                                          <Typography variant="overline">
                                            {document.status}
                                          </Typography>
                                        }
                                        variant="outlined"
                                        sx={{
                                          marginLeft: '10px',
                                          color: '#B42318',
                                          backgroundColor: '#F044381F',
                                        }}
                                      />
                                    ) : 'DUE TODAY' === document?.status ? (
                                      <Chip
                                        label={
                                          <Typography variant="overline">
                                            {document.status}
                                          </Typography>
                                        }
                                        variant="outlined"
                                        sx={{
                                          marginLeft: '10px',
                                          color: '#B54708',
                                          backgroundColor: '#F790091F',
                                        }}
                                      />
                                    ) : 'COMPLETED' === document?.status ? (
                                      <Chip
                                        label={
                                          <Typography variant="overline">
                                            {document.status}
                                          </Typography>
                                        }
                                        variant="outlined"
                                        sx={{
                                          marginLeft: '10px',
                                          color: '#107569',
                                          backgroundColor: 'rgba(21, 183, 158, 0.12)',
                                        }}
                                      />
                                    ) : 'UPCOMING DEADLINE' === document?.status ? (
                                      <Chip
                                        label={
                                          <Typography variant="overline">
                                            {document.status}
                                          </Typography>
                                        }
                                        variant="outlined"
                                        sx={{
                                          marginLeft: '10px',
                                          color: '#6C737F',
                                          backgroundColor: 'rgba(17, 25, 39, 0.08)',
                                        }}
                                      />
                                    ) : (
                                      '-'
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="left">
                                  {/* THIN: this row's therapist === current user → Create
                                      Document; otherwise it isn't mine → Open EOC. */}
                                  {document?.isAssignedToCurrentUser ? (
                                    <Button
                                      title="Create Document"
                                      onClick={() => handleDocumentEdit(document)}
                                      size="small"
                                      sx={{ color: '#6366F1', fontWeight: 600, fontSize: '13px' }}
                                    >
                                      Create Document
                                    </Button>
                                  ) : (
                                    <Button
                                      title="Open EOC"
                                      onClick={() => goToEoc(document)}
                                      size="small"
                                      sx={{ color: '#6366F1', fontWeight: 600, fontSize: '13px' }}
                                    >
                                      Open EOC
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            )
                          );
                        })
                      )}
                    </TableBody>
                  </Table>

                  {(getDocumentRes?.loading || listOfTherapistAvailability?.loading) && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 250,
                        width: '100%',
                      }}
                    >
                      <img
                        src={LoaderIcon}
                        alt="loader.."
                      />
                    </div>
                  )}
                </Box>
              </TableContainer>
            </Stack>
          </>
        </Scrollbar>
      </Card>

      {/* clock in modal */}
      {canShowClockInDialog() && (
        <BootstrapDialog
          PaperProps={{ style: { maxWidth: '750px' } }}
          onClose={() => setIsClockIn(false)}
          aria-labelledby="customized-dialog-title"
          open={isClockIn && !notToShowAgain}
        >
          <ClockInOutModal
            close={handleIsClock}
            handleClockIn={HandleStartClock}
          />
        </BootstrapDialog>
      )}

      {/* Discharge Alert */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '100%' } }}
        onClose={() => setShowDischargeModal(false)}
        aria-labelledby="customized-dialog-title"
        open={showDischargeModal}
      >
        <DischargeRecheckModal
          handleDelete={() => setShowDischargeModal(false)}
          title="and Discharge"
          message={`The following items have not been completed.`}
          buttontext="Got it"
          requiredDocument={actionToBeContinue === 'EDIT_DOCUMENT' ? true : false}
          document={selectedDocument}
          source="DASHBOARD"
          episodeOfCareId={selectedDocument?.episodeOfCareId}
          discipline={selectedDocument?.type}
        />
      </BootstrapDialog>
      <WarningModal
        open={isWarningModalOpen}
        onClose={() => setIsWarningModalOpen(false)}
        title={'Missing Payer Information'}
        message={
          'A payer must be assigned before you can create a certification. Please contact your DOR to update the payer details for this patient.'
        }
        buttonText={'Got it'}
        cancelBtn={'Cancel'}
        handleSaveBtn={() => setIsWarningModalOpen(false)}
      />
      {exportError && (
        <SimpleSnackbar
          message={exportError}
          isError
        />
      )}
    </>
  );
};

export default DocumentManagement;
